import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Pencil, Stethoscope } from "lucide-react";
import { formatDate } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { SpecialtiesForm } from "./SpecialtiesForm";
import { useTRPC } from "@/trpc";
import { Skeleton } from "@/components/ui/skeleton";

export function SpecialtiesCard({
  editable,
  isEditing,
  onEditChange,
  specialties,
  userId,
  isProviderHidden = false,
}: {
  editable: boolean;
  isEditing: boolean;
  onEditChange: (editing: boolean) => void;
  specialties: Array<{
    index: number;
    value: {
      specialty_code: string;
      certifying_board?: string | null;
      state?: string | null;
      certification_number?: string | null;
      initial_certification_date?: string | null;
      expiration_date?: string | null;
    };
  }>;
  userId: string;
  isProviderHidden?: boolean;
}) {
  const trpc = useTRPC();
  const specialtyCodes = specialties
    .filter((specialty) => specialty.value.specialty_code)
    .map((specialty) => specialty.value.specialty_code);
  const {
    data: specialtyData,
    isLoading: isLoadingSpecialties,
    error: specialtiesError,
  } = useQuery(
    trpc.healthcareProfessionalTypes.getByCodes.queryOptions(
      { codes: specialtyCodes },
      {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 2,
      },
    ),
  );

  const getSpecialtyName = (code: string) => {
    return specialtyData?.find((s) => s.code === code)?.name || code;
  };

  return (
    <Card
      className={cn("mt-4", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {specialties.length > 1 ? "Specialties" : "Specialty"}
          </h3>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <SpecialtiesForm
            specialties={specialties}
            userId={userId}
            onSave={() => onEditChange(false)}
          />
        ) : (
          <>
            {isLoadingSpecialties ? (
              <div className="space-y-3">
                {[...new Array(Math.max(1, specialties.length || 1))].map(
                  (_, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3"
                    >
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : specialtiesError ? (
              <div className="text-sm text-red-600">
                Unable to load specialty details.
              </div>
            ) : specialties.length > 0 ? (
              specialties.map((specialty, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Stethoscope className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">
                        {getSpecialtyName(specialty.value.specialty_code)}
                      </div>
                      {specialty.value.certifying_board ? (
                        <div className="text-sm text-gray-600">
                          {specialty.value.certifying_board}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600">
                          No board certification
                        </div>
                      )}
                      {specialty.value.certification_number && (
                        <div
                          className={`text-sm ${isProviderHidden ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {isProviderHidden
                            ? "# Hidden"
                            : `#${specialty.value.certification_number}`}
                        </div>
                      )}
                      {specialty.value.expiration_date && (
                        <div className="text-sm text-gray-500">
                          Exp.{" "}
                          {formatDate(
                            specialty.value.expiration_date,
                            "MMM yyyy",
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500">
                No specialties added yet.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
